import { TransportOperatorService } from 'src/app/angularJS/services/transport-operator/transport-operator.service';
import { UserMessagesService } from 'src/app/angularJS/services/user-messages.service';
import { UserService } from 'src/app/angularJS/services/user/user.service';
import { DialogService } from 'src/app/angularJS/services/dialog/dialog.service';
import { LinkBuilder } from 'src/app/angularJS/services/link-builder.service';
import { BreadcrumbService } from 'src/app/angularJS/services/breadcrumbs/breadcrumb.service';


/**
 * A Controller using the {@link actora.services.transportOperatorService transportOperatorService} to handle the update of transport operators.
 */
export class EditOperatorController {
  public static $inject = [
    'operator',
    'transportOperatorService',
    '$rootScope',
    'UserMessages',
    '$state',
    '$translate',
    'userService',
    'dialogService',
    'LinkBuilder',
    'breadcrumbService'
  ];

  public externalReferences: Array<InterACT.Interfaces.ITransportOperatorExternalReference>;
  public currentLocale: string;

  constructor(
    public operator: InterACT.Interfaces.TransportOperators.TransportOperator,
    private transportOperatorService: TransportOperatorService,
    private $rootScope,
    private UserMessages: UserMessagesService,
    private $state: ng.ui.IStateService,
    private $translate: ng.translate.ITranslateService,
    private userService: UserService,
    private dialogService: DialogService,
    private linkBuilder: LinkBuilder,
    private breadcrumbService: BreadcrumbService
  ) {
    this.activate();
  }

  /**
   * Prepares a transport operator object and supplies to the transport operator data service for updating in the back end service
   */
  public updateOperator = () => {
    this.dialogService.showAuditReasonDialog().then((reason) => {
      this.$rootScope.showSpinner = true;
      this.setOperatorValues();
      this.transportOperatorService.update(this.operator, reason).then(() => {
        this.$rootScope.showSpinner = false;
        const currentLocale = this.$translate.use();
        this.UserMessages.showToast('TOAST_MESSAGE.OPERATOR.EDITED', { name: this.operator.name[currentLocale] });
        this.goToOperator();
      });
    });
  }

  /**
   * Invokes a state change to the operator summary state
   */
  public goToOperator = () => {
    this.$state.go('operator.summary', {
      operatorId: this.operator.id
    }, {
      reload: 'operator'
    });
  }

  /**
   * This function will setup an externalReferences array to be used by the view.
   * In particular this function will build up the array based on existing references already belonging to the operator and also
   * checking each of the schemes the operator is assigned to has a corresponding reference. In the case no matching reference is foucn for an operator's scheme
   * then a new reference object is added.
   */
  private activate() {
    this.operator = angular.copy(this.operator);
    this.currentLocale = this.$translate.use();
    this.externalReferences = <any>angular.copy(this.operator.externalReference);
    this.operator.schemes.forEach((scheme) => {
      if (!this.externalReferenceHasScheme(<any>scheme)) {
        this.externalReferences.push({
          alias: '',
          reference: '',
          scheme: <any>scheme
        });
      }
    });

    this.breadcrumbService.setBreadcrumbs([
      {
        name: 'BREADCRUMBS.REIMBURSEMENT.OPERATORS.OPERATORS',
        sref: 'operators'
      },
      {
        name: 'BREADCRUMBS.REIMBURSEMENT.OPERATORS.EDIT',
        sref: 'operator.edit'
      }
    ]);

    //Must convert all scheme property values to full scheme object representations for use in the view
    this.externalReferences.forEach((reference) => {
      reference.scheme = this.getSchemeFromLink((<any>reference.scheme).link);
    });
  }

  /**
   * Sets the values of the operator ready for updating the service
   */
  private setOperatorValues = () => {
    this.operator.schemes = [];
    this.operator.externalReference = [];

    let referenceCopy;
    this.externalReferences.forEach((reference) => {
      referenceCopy = angular.copy(reference);
      referenceCopy.scheme = {
        abbreviation: reference.scheme.reference,
        link: this.linkBuilder.getSchemesLink(reference.scheme.id)
      };
      this.operator.schemes.push(referenceCopy.scheme);
      this.operator.externalReference.push(referenceCopy);
    });

    if (!this.operator.source) {
      this.operator.source = <any>{};
      this.operator.source.manualEntry = true;
    }

    if (this.operator.fields.hasOwnProperty('OID') && (<any>this.operator.fields).OID === '') {
      delete (<any>this.operator.fields).OID;
    }
    if (this.operator.fields.hasOwnProperty('SPID') && (<any>this.operator.fields).SPID === '') {
      delete (<any>this.operator.fields).SPID;
    }
  }

  /**
   * A helper function used to check whether a given scheme has been used in an array of operator external references;
   *
   * @param scheme A scheme record object
   * @returns A true\false flag indicating whether a reference contains a scheme link for the supplied scheme record object
   */
  private externalReferenceHasScheme = (scheme: InterACT.Interfaces.Users.SchemeLink) => {
    return _.some(this.externalReferences, (reference) => {
      return (<any>reference.scheme).link === scheme.link;
    });
  }

  /**
   * A helper function used to look up a scheme object from the users schemes using a provided scheme link value
   *
   * @param schemeLink A scheme link path typically in the form of '/schemes/:schemeId'
   * @returns The corresponding scheme record object
   */
  private getSchemeFromLink = (schemeLink: string) => {
    return _.find(this.userService.schemes, (scheme) => {
      return scheme.id === schemeLink.split('/').pop();
    });
  }
}